import React, { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import ReviewCards from '../../../components/ReviewCards.jsx';
import {
  INDIVIDUAL_VARIANTS, ROLE_LABELS, TRAINING_LABELS, FACILITY_TYPE_CODES, variantKey,
} from '../../../constants/reviewVariants.js';
import { primaryBtnClass, secondaryBtnClass, saveDraftPatch, prepareCheckout } from '../shared.jsx';
import { getStripePromise } from '../../../components/PaymentForm.jsx';

export default function IndividualReview({ role, training_type, facility_type, plan: initialPlan, publishable_key, pricing, progress }) {
  // Lazy init: honor the saved plan only if it's actually available for this
  // variant + pricing, otherwise pick the first available plan. The controller
  // always passes plan='subscription' as a default, so initialPlan cannot be
  // trusted — 20h variants have no subscription and would render with an
  // unselected radio for one frame until ReviewCards' useEffect corrected it.
  const [plan, setPlan] = useState(() => {
    const v = role && training_type ? INDIVIDUAL_VARIANTS[variantKey(role, training_type)] : null;
    const hasSub = !!(v?.subscription && pricing?.subscription);
    const hasOt  = !!(v?.oneTime && pricing?.one_time);
    if (initialPlan === 'subscription' && hasSub) return 'subscription';
    if (initialPlan === 'one_time' && hasOt) return 'one_time';
    if (hasSub) return 'subscription';
    if (hasOt) return 'one_time';
    return 'subscription';
  });
  const [submitting, setSubmitting] = useState(false);
  const prepareTimer = useRef(null);

  useEffect(() => { getStripePromise(publishable_key); }, [publishable_key]);

  useEffect(() => {
    if (prepareTimer.current) clearTimeout(prepareTimer.current);
    prepareTimer.current = setTimeout(async () => {
      await saveDraftPatch({ individual: { plan } });
      prepareCheckout('individual');
    }, 600);
    return () => { if (prepareTimer.current) clearTimeout(prepareTimer.current); };
  }, [plan]);

  const currentVariant = role && training_type ? INDIVIDUAL_VARIANTS[variantKey(role, training_type)] : null;
  const anyPlanAvailable = !!(pricing?.subscription || pricing?.one_time);

  const handleCheckout = async () => {
    if (submitting || !anyPlanAvailable) return;
    setSubmitting(true);
    await saveDraftPatch({ individual: { plan } });
    router.visit('/signup/individual/checkout');
  };

  const hero = currentVariant?.hero;
  const trainingLabel = currentVariant?.summaryLabel || TRAINING_LABELS[training_type];

  return (
    <SignupLayout progress={progress}>
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">
            {hero?.heading || 'Review your training selection'}
          </h2>
          {hero?.subtitle && (
            <p className="text-sm text-gray-500">{hero.subtitle}</p>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 divide-y divide-gray-100">
          <div className="flex justify-between py-2 text-sm">
            <span className="text-gray-500">Role</span>
            <span className="font-medium text-gray-900">{ROLE_LABELS[role]}</span>
          </div>
          <div className="flex justify-between py-2 text-sm">
            <span className="text-gray-500">Training</span>
            <span className="font-medium text-gray-900" data-test="training-summary">{trainingLabel}</span>
          </div>
          <div className="flex justify-between py-2 text-sm">
            <span className="text-gray-500">Facility Type</span>
            <span className="font-medium text-gray-900">{FACILITY_TYPE_CODES[facility_type]}</span>
          </div>
        </div>

        {currentVariant && (
          <ReviewCards
            variant={currentVariant} pricing={pricing} loading={false}
            selectedPlan={plan} onSelectPlan={setPlan}
            complianceText={pricing?.subscription?.compliance_text ?? pricing?.one_time?.compliance_text}
            facilityCode={FACILITY_TYPE_CODES[facility_type]}
          />
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => router.visit('/signup/individual/facility')} className={`${secondaryBtnClass} flex-1`}>Back</button>
          <button type="button" onClick={handleCheckout} disabled={submitting || !anyPlanAvailable} className={`${primaryBtnClass} flex-1`}>
            {submitting ? 'Preparing checkout…' : 'Continue to Checkout →'}
          </button>
        </div>
      </div>
    </SignupLayout>
  );
}
